import React, { useState, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useConvenios } from '../../contexts/DataContext';
import { useUser } from '../../components/UserContext';
import { usePortalView } from '../../contexts/PortalViewContext';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Input } from '../../components/input';
import { Badge } from '../../components/badge';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';

const ClientPortalConvenios = () => {
  const { conveniosList, conveniosLoading } = useConvenios();
  const { userData } = useUser();
  const { activeConvenioId } = usePortalView();
  const [searchTerm, setSearchTerm] = useState('');

  const accessibleConvenios = useMemo(() => {
    if (!userData?.convenioAccess) return [];
    const accessIds = new Set(Object.keys(userData.convenioAccess));
    return conveniosList.filter((c) => accessIds.has(c.id));
  }, [conveniosList, userData?.convenioAccess]);

  const filteredConvenios = useMemo(() => {
    if (!searchTerm) return accessibleConvenios;
    const lower = searchTerm.toLowerCase();
    return accessibleConvenios.filter((c) => {
      const nombre = c.nombre?.toLowerCase() || '';
      const desc = c.descripcion?.toLowerCase() || '';
      return nombre.includes(lower) || desc.includes(lower);
    });
  }, [accessibleConvenios, searchTerm]);

  if (conveniosLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-azul-surelia" />
      </div>
    );
  }

  // The sidebar context selector makes this list redundant: the active
  // convenio IS the context, so land straight on its detail.
  if (activeConvenioId) {
    return <Navigate to={`/app/portal/convenios/${activeConvenioId}`} replace />;
  }

  // Auto-redirect if only one convenio
  if (accessibleConvenios.length === 1) {
    return <Navigate to={`/app/portal/convenios/${accessibleConvenios[0].id}`} replace />;
  }

  return (
    <div>
      <div className="mb-6">
        <Heading>Convenios</Heading>
        <Text>Convenios de proveedores asociados</Text>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Buscar convenio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {filteredConvenios.length === 0 ? (
        <div className="text-center py-12">
          <Text>No hay convenios disponibles.</Text>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-lg border border-azul-surelia/30 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHead className="bg-gradient-to-r from-azul-surelia to-azul-surelia/95">
                <TableRow>
                  <TableHeader className="text-white font-semibold py-4">Nombre</TableHeader>
                  <TableHeader className="text-white font-semibold py-4">Beneficiarios</TableHeader>
                  <TableHeader className="text-white font-semibold py-4">Contratos</TableHeader>
                  <TableHeader className="text-white font-semibold py-4">Estatus</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredConvenios.map((convenio, index) => (
                  <TableRow
                    key={convenio.id}
                    href={`/app/portal/convenios/${convenio.id}`}
                    className={`cursor-pointer transition-all duration-200 hover:bg-azul-surelia/5 ${
                      index % 2 === 0 ? 'bg-white' : 'bg-beige-suave/15'
                    }`}
                  >
                    <TableCell className="py-4">
                      <span className="font-medium text-azul-surelia">
                        {convenio.nombre || 'Sin nombre'}
                      </span>
                      {convenio.descripcion && (
                        <p className="text-xs text-zinc-500 truncate max-w-xs mt-0.5">
                          {convenio.descripcion}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="py-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-semibold text-sm">
                        {(convenio.beneficiarios || []).length}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold text-sm">
                        {(convenio.contracts || []).length}
                      </span>
                    </TableCell>
                    <TableCell className="py-4">
                      <Badge color={convenio.estatus === 'activo' ? 'green' : 'zinc'}>
                        {convenio.estatus === 'activo' ? 'Activo' : 'Inactivo'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientPortalConvenios;
